<script module>
  import { LocationFragment } from '$lib/Autocomplete.svelte'

  export const i18n = {
    en: {
      YOUTH: 'Youth',
      SENIOR: 'Senior',
      ADULT: 'Adult',
      '%s ADULT': {
        one: '%s adult',
        other: '%s adults'
      },
      '%s SENIOR': {
        one: '%s senior',
        other: '%s seniors'
      },
      '%s YOUTH': {
        one: '%s youth',
        other: '%s youths'
      },
      '%s people': {
        one: '%s person',
        other: '%s people'
      }
    },
    de: {
      YOUTH: 'Jugend',
      SENIOR: 'Senior',
      ADULT: 'Erwachsener',
      '%s ADULT': {
        one: '%s Erwachsener',
        other: '%s Erwachsene'
      },
      '%s SENIOR': {
        one: '%s Senior',
        other: '%s Senioren'
      },
      '%s YOUTH': {
        one: '%s Jugendlicher',
        other: '%s Jugendliche'
      },
      '%s people': {
        one: '%s Person',
        other: '%s Personen'
      },
      'Leaving from': 'Abreise von',
      'Going to': 'Ankunft in',
      'Search city…': 'Stadt suchen…',
      Change: 'Ändern',
      Search: 'Suchen',
      Cancel: 'Abbrechen',
      Done: 'Fertig',
      Close: 'Schließen',
      'Other places in %s': 'Weitere Orte in %s',
      'Popular places': 'Beliebte Orte',
      'Search results': 'Suchergebnisse',
      'age at departure': 'Alter bei Abreise',
      'Please input the %s for each passenger to ensure that you are offered the best available price.':
        'Bitte geben Sie für jeden Passagier das %s an, um den besten verfügbaren Preis zu erhalten.'
    },
    nb: {
      YOUTH: 'Ungdom',
      SENIOR: 'Senior',
      ADULT: 'Voksen',
      '%s ADULT': {
        one: '%s voksen',
        other: '%s voksne'
      },
      '%s SENIOR': {
        one: '%s senior',
        other: '%s seniorer'
      },
      '%s YOUTH': {
        one: '%s ungdom',
        other: '%s ungdommer'
      },
      '%s people': {
        one: '%s person',
        other: '%s personer'
      },
      'Leaving from': 'Reiser fra',
      'Going to': 'Reiser til',
      'Search city…': 'Søk by…',
      Change: 'Endre',
      Search: 'Søk',
      Cancel: 'Avbryt',
      Done: 'Ferdig',
      Close: 'Lukk',
      'Other places in %s': 'Andre steder i %s',
      'Popular places': 'Populære steder',
      'Search results': 'Søkeresultater',
      'age at departure': 'alder ved avreise',
      'Please input the %s for each passenger to ensure that you are offered the best available price.':
        'Angi %s for hver passasjer for å sikre beste tilgjengelige pris.'
    },
    sv: {
      YOUTH: 'Ungdom',
      SENIOR: 'Senior',
      ADULT: 'Vuxen',
      '%s ADULT': {
        one: '%s vuxen',
        other: '%s vuxna'
      },
      '%s SENIOR': {
        one: '%s senior',
        other: '%s seniorer'
      },
      '%s YOUTH': {
        one: '%s ungdom',
        other: '%s ungdomar'
      },
      '%s people': {
        one: '%s person',
        other: '%s personer'
      },
      'Leaving from': 'Reser från',
      'Going to': 'Reser till',
      'Search city…': 'Sök stad…',
      Change: 'Ändra',
      Search: 'Sök',
      Cancel: 'Avbryt',
      Done: 'Klar',
      Close: 'Stäng',
      'Other places in %s': 'Andra platser i %s',
      'Popular places': 'Populära platser',
      'Search results': 'Sökresultat',
      'age at departure': 'ålder vid avresa',
      'Please input the %s for each passenger to ensure that you are offered the best available price.':
        'Ange %s för varje passagerare för att säkerställa att du erbjuds det bästa tillgängliga priset.'
    }
  }
</script>

<script>
  import { browser, dev } from '$app/environment'
  import { page } from '$app/state'
  import { tick } from 'svelte'

  import Datepicker, { getDefaultDate, parseDate } from '$lib/Datepicker.svelte'
  import { i18n as countryNames } from '$lib/countries.js'
  import { register } from '$lib/utils/text.svelte.js'
  import { SELECTOR, capture } from '$lib/capture.js'
  import Passengers from '$lib/Passengers.svelte'
  import State from '$lib/utils/state.svelte.js'
  import { prefix } from '$lib/utils/prefix.js'
  import { gql } from '$lib/graphql.svelte.js'
  import Symbol from '$lib/Symbol.svelte'
  import Button from '$lib/Button.svelte'
  import Image from '$lib/Image.svelte'
  import Autocomplete, {
    preload,
    createAutocompleteBinding
  } from '$lib/Autocomplete.svelte'
  import {
    passengersFromParams,
    passengersToParams,
    getPassengersLabel
  } from '$lib/utils/passengers.js'

  let text = register(i18n, countryNames)

  const autocomplete = createAutocompleteBinding()

  /**
   * @type {{
   *   origin?: Schema.Location,
   *   destination?: Schema.Location,
   *   departureDate?: string,
   *   passengers?: Schema.PassengerPlaceholderInput[],
   *   passengerInputMode?: 'birthDate' | 'age',
   *   isPassholder?: boolean,
   *   enablePassholder?: boolean,
   *   geolocate?: boolean,
   *   bleed?: boolean,
   *   title?: string,
   *   onsearch?: function({
   *     origin: Schema.Location,
   *     destination: Schema.Location,
   *     departureDate: string,
   *     passengers: Schema.PassengerPlaceholderInput[],
   *     isPassholder?: boolean
   *   }): void
   * }}
   */
  let {
    origin = $bindable(),
    destination = $bindable(),
    departureDate = $bindable(
      page.url.searchParams.get('ddate') || getDefaultDate()
    ),
    passengers = $bindable(
      passengersFromParams(page.url.searchParams) || [{ type: 'ADULT' }]
    ),
    passengerInputMode = 'age',
    isPassholder = $bindable(false),
    enablePassholder = false,
    geolocate = false,
    bleed = false,
    title = '',
    onsearch
  } = $props()

  $effect(() => {
    preload(origin?.countryCode)
  })

  $effect(() => {
    preload(destination?.countryCode)
  })

  /** @type {App.GraphQLOperation<{ destinations: { location: Schema.Location }[] }, { countryCode?: string, coordinates: Schema.GeoJsonPoint }>} */
  const GetNearestLocation = gql`
    query FindNearbyDestinations(
      $coordinates: GeoJSONInput
      $countryCode: String
    ) {
      destinations: findDestinations(
        coordinates: $coordinates
        country: $countryCode
        limit: 1
      ) {
        location {
          ...${LocationFragment}
        }
      }
    }
  `

  $effect(() => {
    if (
      browser &&
      !origin &&
      geolocate &&
      ((State.geolocation.countryCode && State.geolocation.coordinates) || dev)
    ) {
      const { countryCode = 'SE', coordinates = [17.95, 59.39] } =
        State.geolocation
      return GetNearestLocation.execute({
        countryCode,
        coordinates: { type: 'Point', coordinates }
      })
    }
  })

  $effect(() => {
    origin = origin || GetNearestLocation.data?.destinations?.[0]?.location
  })

  let panel = $state(page.url.searchParams.get('panel') || '')

  let date = $derived(parseDate(departureDate))
  let passengerLabel = $derived(getPassengersLabel(passengers))

  const onkeydown = (event) => {
    if (event.key === 'Escape') {
      select(null)
    }
  }

  const select = async (name) => {
    panel = name
    resize()
    await tick()
    content.querySelector(`:is(${SELECTOR}):not(.input)`)?.focus()
  }

  const onsubmit = async (event) => {
    const { submitter } = event
    if (submitter.name === 'panel') {
      select(submitter.value)
    }
    if (submitter.name === 'search' && origin && destination) {
      const props = { origin, destination, departureDate, passengers }
      if (enablePassholder) props.isPassholder = isPassholder
      onsearch?.(props)
    }
    event.preventDefault()
  }

  let content
  let input = $state()
  let height = $state()
  let isAnimating = $state(false)
  const resize = async () => {
    if (!content) return

    const { matches: isSmall } = window.matchMedia('(width < 800px)')
    const before = content.getBoundingClientRect()
    if (panel) height = before.height

    await tick()

    isAnimating = true
    const after = content.getBoundingClientRect()
    const animation = content.animate(
      [
        {
          height: `${before.height}px`,
          transform: isSmall
            ? `translateY(${(before.top - after.top) / (isSmall ? 1 : 2)}px)`
            : 'none'
        },
        {
          height: `${after.height}px`,
          transform: isSmall ? `translateY(0px)` : 'none'
        }
      ],
      {
        duration: isSmall ? 500 : 225,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
      }
    )

    await animation.finished
    if (!panel) height = null
    isAnimating = false
    input?.focus()
  }

  let target
  const onmousedown = (event) => {
    target = event.target
  }

  const onmouseup = (event) => {
    if (
      panel &&
      target === event.target &&
      target !== input &&
      target !== content &&
      !content.contains(target)
    ) {
      select(null)
    }
    target = null
  }
</script>

<svelte:document {onkeydown} {onmousedown} {onmouseup} />

<form
  use:capture={{ disabled: !panel }}
  {onsubmit}
  method="GET"
  style:--height={height}
  class="trip-search-form theme-reset"
  class:animating={isAnimating}
  class:active={panel}>
  <div bind:this={content} class="panel {panel || 'summary'}" class:bleed>
    {#if !panel}
      {#if title}<h2 class="title">{title}</h2>{/if}
      <div class="locations">
        <div class="location" class:is-empty={!origin}>
          <div class="thumbnail">
            {#if origin}
              {#if origin.image}
                <Image
                  src={origin.image.url}
                  alt={origin.image.alt || origin.name}
                  sizes="3.5rem"
                  aspect={1}
                  transforms="q_60"
                  variants={[112]}
                  class="img" />
              {:else}
                <Symbol name="pin" />
              {/if}
            {:else}
              <Symbol name="magnifier" />
            {/if}
          </div>
          <div class="details">
            <span class="label">{text`Leaving from`}</span>
            <span class="output">
              {#if origin}
                <span class="trunc">
                  <strong>{origin.name}</strong>
                  {text(origin.countryCode)}
                </span>
              {:else}
                {text`Search city…`}
              {/if}
            </span>
          </div>
          <button name="panel" value="origin" class="change">
            <Symbol name="pencil" size="xs" />
            <span class="hint">{text`Change`}</span>
          </button>
        </div>
        <svg class="divider" width="3" height="10" fill="none">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1.5.25a.75.75 0 01.75.75v.048a.75.75 0 11-1.5 0V1A.75.75 0 011.5.25zm0 3.904a.75.75 0 01.75.75v.097a.75.75 0 01-1.5 0v-.097a.75.75 0 01.75-.75zm0 3.953a.75.75 0 01.75.75v.048a.75.75 0 11-1.5 0v-.048a.75.75 0 01.75-.75z"
            fill="currentColor" />
        </svg>
        <div class="location" class:is-empty={!destination}>
          <div class="thumbnail">
            {#if destination}
              {#if destination.image}
                <Image
                  src={destination.image.url}
                  alt={destination.image.alt || destination.name}
                  sizes="3.5rem"
                  aspect={1}
                  transforms="q_50"
                  variants={[112]}
                  class="img" />
              {:else}
                <Symbol name="pin" />
              {/if}
            {:else}
              <Symbol name="magnifier" />
            {/if}
          </div>
          <div class="details">
            <span class="label">{text`Going to`}</span>
            <span class="output">
              {#if destination}
                <span class="trunc">
                  <strong>{destination.name}</strong>
                  {text(destination.countryCode)}
                </span>
              {:else}
                {text`Search city…`}
              {/if}
            </span>
          </div>
          <button name="panel" value="destination" class="change">
            <Symbol name="pencil" size="xs" />
            <span class="hint">{text`Change`}</span>
          </button>
        </div>
      </div>
      {#if enablePassholder}
        <label class="checkbox" for="passHolder">
          <input
            class="field"
            type="checkbox"
            id="passHolder"
            name="passHolder"
            bind:checked={isPassholder} />
          {text`Search for passholder fares`}
        </label>
      {/if}
      <ul class="actions">
        <li class="action">
          <Button size="sm" adapt name="panel" value="passengers">
            <Symbol name="people" size="md" adapt />
            <span class="hint hint--inverse">
              {passengers.length}
            </span>
            <span class="hint">
              {text(`%s ${passengerLabel}`, passengers.length)}
            </span>
          </Button>
        </li>
        <li class="action">
          <Button size="sm" adapt name="panel" value="ddate">
            <Symbol name="calendar" prefix size="md" adapt />
            {date.toLocaleString(State.language, {
              month: 'short',
              day: 'numeric'
            })}
          </Button>
        </li>
        <li class="action">
          <Button
            size="sm"
            name="search"
            collapse
            formaction="{$prefix}/search">
            <span class="submit">
              <Symbol name="magnifier" size="md" adapt prefix />
              {text`Search`}
            </span>
          </Button>
        </li>
      </ul>
    {/if}
    {#if panel === 'origin' || panel === 'destination'}
      <div class="scrollable">
        <div class="search">
          <div class="group">
            <label class="label">
              <span class="u-hidden">{text`Search city…`}</span>
              <input
                use:autocomplete
                bind:this={input}
                type="search"
                class="input"
                minlength="2"
                autocorrect="off"
                autocomplete="off"
                autocapitalize="off"
                enterkeyhint="enter"
                name="{panel}-input"
                placeholder={text`Search city…`} />
            </label>
            <noscript>
              <Button size="xs" type="submit">
                {text`Search`}
              </Button>
            </noscript>
            <button value="" name="panel" class="change">
              <Symbol name="cross" size="xs" prefix />
              {text`Cancel`}
            </button>
          </div>
        </div>
        <Autocomplete
          fallbackCountryCode={panel === 'origin'
            ? origin?.countryCode
            : destination?.countryCode}
          onupdate={resize}
          oncancel={() => select(null)}
          onselect={(location) => {
            if (panel === 'origin') origin = location
            else destination = location
            select(null)
          }} />
      </div>
      {#if panel === 'origin' && destination}
        <input type="hidden" name="destination" value={destination.uid} />
      {:else if panel === 'destination' && origin}
        <input type="hidden" name="origin" value={origin.uid} />
      {/if}
    {:else}
      {#if origin}
        <input type="hidden" name="origin" value={origin.uid} />
      {/if}
      {#if destination}
        <input type="hidden" name="destination" value={destination.uid} />
      {/if}
    {/if}
    {#if panel === 'passengers'}
      {@const hasPlaceholderPassenger =
        passengerInputMode === 'birthDate' &&
        passengers.length === 1 &&
        passengers[0].type === 'ADULT'}
      <div class="header">
        <h2 class="heading">
          <Symbol name="people" prefix />
          {#if passengers.length === 0}
            {text`Select passengers`}
          {:else}
            {text(`%s ${passengerLabel}`, passengers.length)}
          {/if}
        </h2>
        <Button
          size="xs"
          name="panel"
          value=""
          formnovalidate={hasPlaceholderPassenger}>
          <Symbol name="checkmark" size="xs" prefix />
          {text`Done`}
        </Button>
      </div>
      <Passengers
        bind:passengers
        onchange={resize}
        inputMode={passengerInputMode} />
      <input type="hidden" name="panel" value="passengers" />
    {/if}
    {#if passengers.length}
      {#each passengersToParams(passengers) as value}
        <input type="hidden" name="passenger" {value} />
      {/each}
    {/if}
    {#if panel === 'ddate'}
      <div class="header">
        <h2 class="heading">
          <Symbol name="calendar" prefix />
          {date.toLocaleString(State.language, {
            month: 'long',
            day: 'numeric'
          })}
        </h2>
        <Button size="xs" name="panel" value="">
          <Symbol name="checkmark" size="xs" prefix />
          {text`Close`}
        </Button>
      </div>
      <Datepicker
        name="ddate"
        bind:value={departureDate}
        on:view={resize}
        on:change={() => select(null)} />
    {:else if departureDate}
      <input type="hidden" name="ddate" value={departureDate} />
    {/if}
  </div>
</form>

<style>
  .trip-search-form {
    --current-color: rgb(var(--color-text));
    --current-background: var(--background);

    display: flex; /** Enable scroll in panel */
    width: 100%;
    max-height: min(35rem, 75vh);
  }

  @media (width <= 800px) {
    .trip-search-form {
      height: calc(var(--height) * 1px);
    }

    :root:has(.trip-search-form.active) {
      overflow: hidden;
    }

    .trip-search-form.active::before {
      content: '';
      width: 100%;
      height: 100%;
      position: fixed;
      left: 0;
      top: 0;
      z-index: 2;
      background-color: rgba(31, 31, 34, 0.65);
      animation: fade-in 500ms forwards cubic-bezier(0.22, 1, 0.36, 1);
    }

    @keyframes fade-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
  }

  /* Title */
  .title {
    font-weight: bold;
    line-height: 1.25;
    font-size: 2rem;
    font-family: var(--heading-font-family);
    color: rgb(var(--rgb-text));
    word-spacing: -0.05em;
    letter-spacing: -0.025em;
  }

  /** Panel */

  .panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
    overflow: hidden;
    color: rgb(var(--rgb-text));
    text-align: left;
    user-select: none;
    container-type: inline-size;
  }

  .panel:not(.bleed) {
    background-color: rgb(var(--rgb-white));
    border-radius: var(--border-radius-lg);
    padding: clamp(1rem, 4cqw, 1.75rem);
  }

  .trip-search-form:not(.animating) .panel:not(.bleed) {
    box-shadow: var(--box-shadow-lg);
  }

  @container (width > 50rem) {
    .panel:not(.bleed) {
      border-radius: 2rem;
    }
  }

  .panel.summary {
    gap: 1.5rem;
  }

  .panel:not(.summary) {
    gap: 1.5rem;
  }

  @media (width <= 800px) {
    .trip-search-form.active .panel {
      width: calc(100dvw - 1rem * 2);
      height: calc(100dvh - 1rem * 2);
      gap: 2rem;
      position: fixed;
      top: 1rem;
      left: 1rem;
      z-index: 15;
      overflow: auto;
      box-shadow: none;
      background: var(--background);
    }

    .trip-search-form.active .panel.bleed {
      border-radius: var(--border-radius-lg);
      padding: clamp(1rem, 4cqw, 1.75rem);
    }
  }

  .header {
    display: flex;
    align-items: center;
    line-height: 1;
    letter-spacing: -0.01em;
  }

  .heading {
    display: flex;
    gap: 0.2rem;
    align-items: center;
    width: 100%;
    margin-right: auto;
    position: static;
    opacity: 1;
    font-size: 1.25rem;
    font-weight: bold;
  }

  /** Location */

  .location {
    position: relative;
    width: 100%;
    display: flex;
    text-align: left;
    align-items: center;
    font-size: 1rem;
    letter-spacing: -0.01;
    cursor: pointer;
  }

  .location.is-empty {
    color: rgb(var(--rgb-muted));
  }

  .output {
    display: flex;
    width: 100%;
    text-align: left;
    line-height: 1.15;
  }

  .trunc {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .divider {
    display: block;
    margin: 0.3125rem 0 0.3125rem 1.15625rem;
  }

  .label {
    display: block;
    font-size: 0.875rem;
    letter-spacing: 0;
    color: rgb(var(--rgb-text));
  }

  .details {
    flex-grow: 1;
    white-space: nowrap;
    overflow: hidden;
  }

  @media (width > 600px) {
    .location {
      font-size: 1.35rem;
    }

    .location.is-empty {
      color: rgba(var(--rgb-muted), 0.9);
    }

    .label {
      font-size: 1rem;
      margin-top: -0.1rem;
    }

    .divider {
      margin: 0.4063rem 0 0.4063rem 1.53125rem;
    }
  }

  /** Thumbnail & image */

  .thumbnail {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    color: #fff;
    position: relative;
    width: 2.5rem;
    height: 2.5rem;
    margin-right: 0.625rem;
    border-radius: 0.5rem;
    background-color: rgb(var(--rgb-gray));
  }

  .thumbnail :global(.img) {
    object-fit: cover;
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    border-radius: 0.5rem;
  }

  @media (width > 600px) {
    .thumbnail {
      width: 3.25rem;
      height: 3.25rem;
      margin-right: 0.75rem;
      border-radius: 0.75rem;
    }

    .thumbnail :global(.img) {
      border-radius: 0.75rem;
    }
  }

  /** Hints (long/short) */

  .hint {
    margin-left: 0.4rem;
  }

  @media (width <= 600px), (width > 800px) and (width <= 1150px) {
    .hint:not(.hint--inverse) {
      margin: 0;
      font-size: 0;
      color: transparent;
      position: absolute;
    }
  }

  @media (width > 600px) and (width <= 800px), (width > 1150px) {
    .hint--inverse {
      margin: 0;
      font-size: 0;
      color: transparent;
      position: absolute;
    }
  }

  .checkbox {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    user-select: none;
    font-size: 0.875rem;
    cursor: pointer;
    text-wrap: balance;
  }

  .checkbox .field {
    flex: 0 0 auto;
    width: 1.25em;
    height: 1.25em;
    padding: 0;
    border: 1px solid rgba(var(--rgb-muted), 0.4);
    cursor: pointer;
    appearance: none;
    background: var(--background);
    box-shadow: var(--box-shadow-sm);
    border-radius: var(--border-radius-xs);
    transition: var(--box-shadow-transition);
  }

  .checkbox .field:checked {
    background-image: url('$lib/assets/checkmark.svg');
    background-size: 100%;
    background-repeat: no-repeat;
    background-position: 0 2px;
  }

  .checkbox .field:focus {
    border-color: rgba(var(--rgb-muted), 0.5);
    box-shadow: var(--box-shadow-sm-hover);
  }

  .checkbox:hover .field {
    box-shadow: var(--box-shadow-sm-hover);
  }

  .checkbox:active .field {
    box-shadow: var(--box-shadow-sm-active);
  }

  @media (width > 600px) {
    .checkbox {
      font-size: 1rem;
    }
  }

  /** Buttons */

  .change {
    display: flex;
    align-items: center;
    padding: 0.5rem;
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    user-select: none;
    font-weight: 600;
    letter-spacing: 0;
    font-size: 0.875rem;
    color: rgb(var(--rgb-muted));
    background: var(--background-alt);
    border-radius: var(--border-radius-full);
    box-shadow: var(--box-shadow-sm);
    transition: var(--box-shadow-transition), var(--color-transition);
  }

  .change::before {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
  }

  .location:hover .change,
  .change:hover {
    box-shadow: var(--box-shadow-sm-hover);
  }

  .location:active .change,
  .change:active {
    box-shadow: var(--box-shadow-sm-active);
  }

  @media (width > 600px) and (width <= 800px), (width > 1150px) {
    .change {
      padding: 0.45rem 0.7rem 0.45rem 0.6rem;
    }
  }

  /** Action buttons */

  .actions {
    display: flex;
    align-items: center;
    gap: clamp(0.25rem, 1.5cqw, 1rem);
  }

  @container (width < 30rem) {
    .actions {
      --current-font-size: 0.875rem;
    }
  }

  .action:last-child {
    margin-left: auto;
  }

  .submit {
    padding: 0.6rem 1rem;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  @media (width > 600px) and (width <= 800px), (width > 1150px) {
    .submit {
      padding: 1rem 1.7rem;
      font-size: 1.25rem;
    }
  }

  /** Passengers */

  .footer {
    margin-top: auto;
    color: rgb(var(--rgb-muted));
    font-size: 1rem;
    text-wrap: balance;
  }

  /** Autocomplete */

  .scrollable {
    position: relative;
    overflow: hidden;
    margin: 0 -0.5rem 1.75rem;
    padding: 0 0.5rem;
  }

  .trip-search-form:not(.animating) .scrollable {
    overflow: auto;
  }

  .panel.origin,
  .panel.destination {
    padding-bottom: 0;
  }

  .panel.origin::after,
  .panel.destination::after {
    content: '';
    width: 100%;
    height: 1.5rem;
    position: absolute;
    bottom: 0;
    left: 0;
    pointer-events: none;
    background: linear-gradient(
      to top,
      rgb(var(--rgb-white), 1) 0%,
      rgb(var(--rgb-white), 1) 10%,
      rgb(var(--rgb-white), 0.8) 40%,
      rgba(var(--rgb-white), 0)
    );
  }

  .search {
    position: sticky;
    margin: 0 -0.15rem 1.25rem -0.15rem;
    top: 0;
    z-index: 1;
    background: rgb(var(--rgb-white));
  }

  .search::before {
    content: '';
    width: 100%;
    height: 200%;
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: -1;
    background: rgb(var(--rgb-white));
  }

  .search::after {
    content: '';
    width: 100%;
    height: 1rem;
    position: absolute;
    top: 100%;
    left: 0;
    pointer-events: none;
    background: linear-gradient(
      to bottom,
      rgb(var(--rgb-white), 1) 0%,
      rgb(var(--rgb-white), 1) 10%,
      rgb(var(--rgb-white), 0.8) 40%,
      rgba(var(--rgb-white), 0)
    );
  }

  .group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.5rem 0.5rem 1.25rem;
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    position: relative;
    z-index: 1;
    color: rgb(var(--rgb-muted));
    background: rgb(var(--rgb-white));
    border-radius: var(--border-radius-full);
    box-shadow: 0 0.125rem 0.25rem rgba(var(--rgb-blue), 0.05);
  }

  .label {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .input {
    flex: 1 1 auto;
    width: 100%;
    appearance: none;
    border: 0;
    font-size: 1.125rem;
    background: transparent;
  }

  .input::placeholder {
    opacity: 1;
    color: rgb(var(--rgb-muted));
  }

  :global(.using-keyboard) .input:focus-visible {
    outline-offset: 0.3rem;
    border-radius: var(--border-radius-full);
  }

  .search .change {
    padding: 0.45rem 0.7rem 0.45rem 0.6rem;
    line-height: 1;
  }

  .search .change::before {
    content: none;
  }
</style>
